import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ChevronLeft, ChevronRight, Eye, EyeOff, Users, Copy, Loader2,
  Layers, FileText, ListChecks, Timer, Play, Square, Trophy, Send, MessageCircle } from "lucide-react";
import RoomChat, { mergeMessages } from "@/components/RoomChat";
import { toast } from "sonner";
import api, { getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const RATINGS = [
  { key: "again", label: "Again", color: "#FCA5A5", bg: "rgba(252, 165, 165, 0.10)", bg2: "rgba(252, 165, 165, 0.25)" },
  { key: "hard", label: "Hard", color: "#B8A4FF", bg: "rgba(184, 164, 255, 0.10)", bg2: "rgba(184, 164, 255, 0.25)" },
  { key: "good", label: "Good", color: "#7C5CFF", bg: "rgba(124, 92, 255, 0.10)", bg2: "rgba(124, 92, 255, 0.25)" },
  { key: "easy", label: "Easy", color: "#5AC8FA", bg: "rgba(90, 200, 250, 0.10)", bg2: "rgba(90, 200, 250, 0.25)" },
];

export default function RoomSession() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [room, setRoom] = useState(null);
  const [content, setContent] = useState({ flashcards: [], notes_markdown: "", quiz_questions: [], kind: "study", title: "" });
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [members, setMembers] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reactions, setReactions] = useState([]);
  const [tab, setTab] = useState("cards");
  const [chat, setChat] = useState([]);
  const [unread, setUnread] = useState(0);
  const tabRef = useRef("cards");
  useEffect(() => { tabRef.current = tab; if (tab === "chat") setUnread(0); }, [tab]);

  // ── In-room timed exam state ──
  const [exam, setExam] = useState(null); // {exam_id, questions, ends_at, duration_sec}
  const [examPhase, setExamPhase] = useState("idle"); // idle | active | done
  const [examLeaderboard, setExamLeaderboard] = useState([]);
  const [myResult, setMyResult] = useState(null); // {score, total}

  const wsRef = useRef(null);
  const cardsLenRef = useRef(0);
  const isOwner = room?.owner_id === user?.id;
  const cards = content.flashcards;

  useEffect(() => { cardsLenRef.current = cards.length; }, [cards.length]);

  // Load room info + shared content (room-scoped — works for non-owners)
  useEffect(() => {
    const init = async () => {
      try {
        const r = await api.get(`/rooms/join/${code}`);
        setRoom(r.data);
        setMembers(r.data.online || []);
        const c = await api.get(`/rooms/${code}/content`);
        setContent(c.data || {});
        api.get(`/rooms/${code}/messages`).then((m) => setChat(m.data.messages || [])).catch(() => {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Room not found");
        nav("/app/rooms");
      } finally { setLoading(false); }
    };
    init();
  }, [code]);

  // WebSocket
  useEffect(() => {
    if (!room || !user) return;
    const token = getToken();
    if (!token) return;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = baseUrl.replace(/^http/, "ws") + `/api/rooms/${code}/ws?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "welcome" || msg.type === "user_joined" || msg.type === "user_left") {
          setMembers(msg.members || []);
          if (msg.type === "user_joined" && msg.user_id !== user.id) toast.info(`${msg.name} joined`);
          if (msg.type === "user_left" && msg.user_id !== user.id) toast(`${msg.name} left`, { icon: "👋" });
          // Late join during an active exam
          if (msg.type === "welcome" && msg.exam) {
            setExam(msg.exam);
            setExamLeaderboard(msg.exam.leaderboard || []);
            setExamPhase("active");
            setTab("exam");
          }
        }
        if (msg.type === "exam_started") {
          setExam({ exam_id: msg.exam_id, questions: msg.questions, ends_at: msg.ends_at, duration_sec: msg.duration_sec });
          setExamLeaderboard([]);
          setMyResult(null);
          setExamPhase("active");
          setTab("exam");
          toast.info(`${msg.started_by_name} started a timed exam!`, { icon: "⏱️" });
        }
        if (msg.type === "exam_result") {
          setExamLeaderboard(msg.leaderboard || []);
          if (msg.user_id !== user.id) toast(`${msg.name} finished — ${msg.score}/${msg.total}`, { icon: "✅" });
        }
        if (msg.type === "exam_ended") {
          setExamLeaderboard(msg.leaderboard || []);
          // Reset back to the config/waiting screen so a new exam can be started.
          setExam(null);
          setMyResult(null);
          setExamPhase("idle");
          toast(`Exam ended`, { icon: "🏁" });
        }
        if (msg.type === "exam_error") {
          toast.error(msg.message || "Could not start the exam.");
        }
        if (msg.type === "state_change") {
          if (msg.sender && msg.sender === user.id) return; // ignore echo of our own action
          const len = cardsLenRef.current;
          if (msg.action === "flip") {
            setFlipped(!!msg.flipped);
          } else {
            if (typeof msg.index === "number")
              setIndex(Math.min(Math.max(0, msg.index), Math.max(0, len - 1)));
            setFlipped(false);
          }
        }
        if (msg.type === "reaction") {
          const id = Math.random();
          setReactions((r) => [...r, { emoji: msg.emoji, name: msg.name, id }]);
          setTimeout(() => setReactions((r) => r.filter((x) => x.id !== id)), 3000);
        }
        if (msg.type === "chat" && msg.message) {
          setChat((m) => mergeMessages(m, [msg.message]));
          if (tabRef.current !== "chat" && msg.message.user_id !== user.id) setUnread((n) => n + 1);
        }
        if (msg.type === "room_ended") { toast.error("Room has ended"); nav("/app/rooms"); }
      } catch {}
    };
    return () => { ws.close(); };
  }, [room, user]);

  const send = (payload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify(payload));
  };

  const handleFlip = () => { if (!isOwner) return; const nf = !flipped; setFlipped(nf); send({ type: "flip", flipped: nf }); };
  const handleNext = () => {
    if (!isOwner || index >= cards.length - 1) return;
    const ni = index + 1; setIndex(ni); setFlipped(false); send({ type: "next", index: ni });
  };
  const handlePrev = () => {
    if (!isOwner || index <= 0) return;
    const ni = index - 1; setIndex(ni); setFlipped(false); send({ type: "prev", index: ni });
  };
  const sendReaction = (emoji) => {
    send({ type: "reaction", emoji });
    const id = Math.random();
    setReactions((r) => [...r, { emoji, name: "You", id }]);
    setTimeout(() => setReactions((r) => r.filter((x) => x.id !== id)), 3000);
  };
  const copyCode = () => { navigator.clipboard.writeText(code); toast.success(`Code ${code} copied!`); };

  // ── Exam actions ──
  const startExam = (duration_sec, question_count) => {
    send({ type: "exam_start", duration_sec, question_count });
  };
  const submitExam = (score, total) => {
    if (!exam) return;
    send({ type: "exam_submit", exam_id: exam.exam_id, score, total });
    setMyResult({ score, total });
    setExamPhase("done");
  };
  const endExam = () => { send({ type: "exam_end" }); };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 size={28} className="animate-spin text-[#B8A4FF]" /></div>
  );

  const card = cards[index];
  const TABS = [
    { key: "cards", label: "Flashcards", icon: Layers },
    { key: "notes", label: "Notes", icon: FileText },
    { key: "quiz", label: "Quiz", icon: ListChecks },
    { key: "exam", label: "Exam", icon: Timer },
    { key: "chat", label: unread ? `Chat (${unread})` : "Chat", icon: MessageCircle },
  ];

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-5 py-6 sm:py-8" data-testid="room-session">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/app/rooms" className="btn-icon text-[#B0AEBC] hover:text-white flex-shrink-0"><ArrowLeft size={18} /></Link>
          <div className="min-w-0">
            <h1 className="text-[18px] font-semibold truncate">{content.title || room?.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <div className={`w-2 h-2 rounded-full ${connected ? "bg-green-400" : "bg-red-400"}`} />
              <span className="text-[12px] text-[#B0AEBC]">{connected ? "Live" : "Reconnecting…"}</span>
              <button onClick={copyCode} className="flex items-center gap-1 text-[12px] text-[#B0AEBC] hover:text-[#B8A4FF] transition-colors ml-1" data-testid="room-copy-code">
                <span className="font-mono tracking-widest font-semibold">{code}</span><Copy size={11} />
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06] flex-shrink-0">
          <Users size={13} className="text-[#B8A4FF]" />
          <span className="text-[12px] text-white font-medium">{members.length}</span>
        </div>
      </div>

      {/* Members */}
      {members.length > 0 && (
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          {members.map((m) => (
            <span key={m.user_id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px]"
              style={{ background: "rgba(184,164,255,0.10)", border: "1px solid rgba(184,164,255,0.22)", color: "#B8A4FF" }}>
              <span className="w-4 h-4 rounded-full bg-[#B8A4FF] text-[#0A0A0B] text-[9px] font-bold flex items-center justify-center">
                {(m.name || "?")[0]}
              </span>
              {m.name}
              {m.user_id === room?.owner_id && <span className="text-[9px] font-bold text-[#F59E0B]">HOST</span>}
            </span>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="pill-tabs inline-flex p-1 rounded-full mb-6" data-testid="room-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`room-tab-${t.key}`}
            className="pill-tab flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-medium"
            data-state={tab === t.key ? "active" : "inactive"}
            style={tab === t.key ? { background: "rgba(124,92,255,0.16)", color: "#fff" } : {}}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── Flashcards (synced) ── */}
      {tab === "cards" && (
        cards.length === 0 ? (
          <div className="text-center py-16 text-[#B0AEBC]">No flashcards in this {content.kind || "study"}.</div>
        ) : (
          <>
            <div className="text-[12px] text-[#B0AEBC] text-center mb-3 tabular-nums">{index + 1} / {cards.length}</div>
            <div className="relative mb-5 cursor-pointer" onClick={isOwner ? handleFlip : undefined}
              data-testid="room-flashcard" style={{ height: 300, perspective: 1600 }}>
              <AnimatePresence mode="wait" initial={false}>
                {!flipped ? (
                  <motion.div key={`f-${index}`}
                    initial={{ rotateY: 90, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }} exit={{ rotateY: -90, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="surface absolute inset-0 p-8 flex flex-col items-center justify-center text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-widest mb-4 text-[#8E8B99]">
                      Question
                      {card?.subcourse_title && <span className="ml-2 normal-case tracking-normal">· {card.subcourse_title}</span>}
                    </p>
                    <p className="text-[20px] text-white leading-relaxed font-medium">{card?.front}</p>
                    <p className="absolute bottom-4 text-[11px] text-[#8E8B99]">{isOwner ? "Tap to flip" : "Waiting for host to flip"}</p>
                  </motion.div>
                ) : (
                  <motion.div key={`b-${index}`}
                    initial={{ rotateY: -90, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }} exit={{ rotateY: 90, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 p-8 flex flex-col items-center justify-center text-center text-white rounded-[18px] overflow-hidden"
                    style={{ background: "linear-gradient(135deg, #7C5CFF 0%, #B8A4FF 50%, #7C5CFF 100%)" }}>
                    <div className="absolute inset-0 grid-lines opacity-15" />
                    <p className="relative text-[11px] font-semibold uppercase tracking-widest mb-4 opacity-80">Answer</p>
                    <p className="relative text-[18px] leading-relaxed font-medium">{card?.back}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {isOwner ? (
              <div className="flex items-center justify-center gap-3 mb-4">
                <button onClick={handlePrev} disabled={index === 0} data-testid="room-prev-btn"
                  className="btn-icon w-11 h-11 disabled:opacity-30 rounded-xl bg-white/[0.04] border border-white/[0.08]"><ChevronLeft size={20} /></button>
                <button onClick={handleFlip} data-testid="room-flip-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-[13px] font-medium bg-white/[0.06] border border-white/[0.10] hover:bg-white/[0.10] transition-colors">
                  {flipped ? <EyeOff size={14} /> : <Eye size={14} />}{flipped ? "Hide answer" : "Reveal answer"}
                </button>
                <button onClick={handleNext} disabled={index >= cards.length - 1} data-testid="room-next-btn"
                  className="btn-icon w-11 h-11 disabled:opacity-30 rounded-xl bg-white/[0.04] border border-white/[0.08]"><ChevronRight size={20} /></button>
              </div>
            ) : (
              <p className="text-center text-[12px] text-[#8E8B99] mb-4">The host controls the deck — follow along live.</p>
            )}

            {/* Recall rating — matches the personal study review UI */}
            <div className="text-center text-[12px] text-[#8E8B99] mb-3">
              {flipped ? "Rate your recall · 1/2/3/4" : "Reveal the answer to rate your recall"}
            </div>
            <div className="grid grid-cols-4 gap-2 mb-4 max-w-xl mx-auto">
              {RATINGS.map((r, i) => (
                <motion.button
                  key={r.key}
                  onClick={() => { if (flipped && isOwner) handleNext(); }}
                  disabled={!flipped || !isOwner}
                  whileHover={flipped && isOwner ? { y: -4 } : {}}
                  whileTap={flipped && isOwner ? { scale: 0.95 } : {}}
                  data-testid={`room-rate-${r.key}`}
                  className="rounded-2xl py-3.5 px-2 transition-all disabled:opacity-30 disabled:cursor-not-allowed border"
                  style={{
                    background: flipped ? r.bg : "rgba(255,255,255,0.04)",
                    borderColor: flipped ? r.bg2 : "rgba(255,255,255,0.08)",
                    boxShadow: flipped ? `0 0 24px -8px ${r.color}` : "none",
                  }}
                >
                  <div className="text-[10px] font-bold text-[#8E8B99] mb-0.5">{i + 1}</div>
                  <div className="font-bold text-[14px]" style={{ color: flipped ? r.color : "#71717A" }}>{r.label}</div>
                </motion.button>
              ))}
            </div>
          </>
        )
      )}

      {/* ── Notes ── */}
      {tab === "chat" && (
        <RoomChat code={code} userId={user?.id} messages={chat} setMessages={setChat} connected={connected} />
      )}

      {tab === "notes" && (
        <div className="surface p-6 sm:p-8" data-testid="room-notes">
          {content.notes_markdown ? (
            <div className="prose-apple max-w-none text-[15px] leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.notes_markdown}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-[#B0AEBC] text-center py-10">No notes available for this {content.kind || "study"}.</p>
          )}
        </div>
      )}

      {/* ── Quiz (individual practice) ── */}
      {tab === "quiz" && (
        <div data-testid="room-quiz">
          {(content.quiz_questions || []).length === 0 ? (
            <p className="text-[#B0AEBC] text-center py-10">No quiz questions available.</p>
          ) : (
            <div className="space-y-4">
              <p className="text-[12.5px] text-[#8E8B99] mb-1">Practice solo — answers reveal on tap. Everyone has their own copy.</p>
              {content.quiz_questions.map((q, i) => <QuizCard key={i} q={q} i={i} />)}
            </div>
          )}
        </div>
      )}

      {/* ── Exam (timed, synced) ── */}
      {tab === "exam" && (
        <RoomExam
          exam={exam}
          phase={examPhase}
          isOwner={isOwner}
          quizCount={(content.quiz_questions || []).length}
          leaderboard={examLeaderboard}
          myResult={myResult}
          currentUserId={user?.id}
          onStart={startExam}
          onSubmit={submitExam}
          onEnd={endExam}
        />
      )}

      {/* Floating reactions */}
      <div className="fixed bottom-24 right-6 space-y-1 pointer-events-none z-[60]">
        <AnimatePresence>
          {reactions.map((r) => (
            <motion.div key={r.id} initial={{ opacity: 0, y: 20, scale: 0.5 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -30 }} className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[13px]"
              style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)" }}>
              <span className="text-base">{r.emoji}</span><span className="text-white text-[11px]">{r.name}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function QuizCard({ q, i }) {
  const [show, setShow] = useState(false);
  const nq = normalizeQuestion(q);
  const correctText = (() => {
    const c = (nq.correct || "").trim();
    if (!c) return "";
    const match = nq.options.find(
      (o) => o.label.toLowerCase() === c.toLowerCase() || o.text.trim().toLowerCase() === c.toLowerCase()
    );
    return match ? `${match.label}. ${match.text}` : c;
  })();
  const isCorrect = (o) => {
    const c = (nq.correct || "").trim().toLowerCase();
    if (!c) return false;
    return c === o.label.toLowerCase() || c === o.text.trim().toLowerCase();
  };
  return (
    <div className="surface p-5" data-testid={`room-quiz-card-${i}`}>
      <div className="flex gap-2.5">
        <span className="text-[13px] font-bold text-[#B8A4FF] tabular-nums">{i + 1}.</span>
        <p className="text-[15px] font-medium leading-snug flex-1">{nq.text}</p>
      </div>
      {nq.options.length > 0 && (
        <ul className="mt-3 space-y-1.5 pl-7">
          {nq.options.map((o, j) => (
            <li key={j} className="text-[13.5px] text-[#B0AEBC] flex items-center gap-2">
              <span className="text-[#8E8B99] font-mono">{o.label}.</span>
              <span className={show && isCorrect(o) ? "text-[#34D399] font-semibold" : ""}>{o.text}</span>
            </li>
          ))}
        </ul>
      )}
      <button onClick={() => setShow((s) => !s)} className="text-[12px] font-semibold text-[#B8A4FF] mt-3 pl-7 hover:underline"
        data-testid={`room-quiz-reveal-${i}`}>
        {show ? "Hide answer" : "Show answer"}
      </button>
      {show && (
        <div className="mt-2 pl-7 text-[13.5px]">
          {correctText && <p className="text-[#34D399] font-medium">Answer: {correctText}</p>}
          {nq.explanation && <p className="text-[#B0AEBC] mt-1 leading-relaxed">{nq.explanation}</p>}
        </div>
      )}
    </div>
  );
}


// Normalize a quiz question into a consistent shape for the exam.
function normalizeQuestion(q) {
  const text = q.text || q.question || q.prompt || q.q || "";
  const rawOpts = q.options || q.choices || [];
  const options = rawOpts.map((o, j) => {
    const label = (typeof o === "object" && o?.label) ? String(o.label) : String.fromCharCode(65 + j);
    const otext = (typeof o === "object") ? (o.text ?? o.value ?? "") : String(o);
    return { label, text: otext };
  });
  let type = q.type || (options.length > 0 ? "mcq" : "short");
  if (type === "tf" || type === "true_false" || type === "truefalse") type = "tf";
  let correct = q.correct_answer ?? q.answer ?? q.correct ?? null;
  if (correct == null && typeof q.correct_index === "number" && options[q.correct_index])
    correct = options[q.correct_index].label;
  return { text, type, options, correct: correct == null ? "" : String(correct), explanation: q.explanation || q.rationale || "" };
}

// Grade a single answer. Returns true if correct.
function gradeAnswer(nq, answer) {
  if (answer == null || String(answer).trim() === "") return false;
  const a = String(answer).trim().toLowerCase();
  const c = String(nq.correct).trim().toLowerCase();
  if (nq.type === "mcq") {
    // answer is the chosen option label; also accept matching option text
    if (a === c) return true;
    const chosen = nq.options.find((o) => o.label.toLowerCase() === a);
    return chosen ? chosen.text.trim().toLowerCase() === c : false;
  }
  if (nq.type === "tf") return a === c || a[0] === c[0];
  // short answer — lenient: exact match or correct text contained
  return a === c || (c.length > 2 && a.includes(c)) || (a.length > 2 && c.includes(a));
}

function RoomExam({ exam, phase, isOwner, quizCount, leaderboard, myResult, currentUserId, onStart, onSubmit, onEnd }) {
  const [duration, setDuration] = useState(600);
  const [count, setCount] = useState(8);
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(0);
  const submittedRef = useRef(false);

  const questions = (exam?.questions || []).map(normalizeQuestion);

  // Reset local answers when a new exam starts
  useEffect(() => {
    if (exam?.exam_id) { setAnswers({}); submittedRef.current = false; }
  }, [exam?.exam_id]);

  // Synced countdown driven by the server's ends_at timestamp
  useEffect(() => {
    if (phase !== "active" || !exam?.ends_at) return;
    const tick = () => {
      const secs = Math.max(0, Math.round((new Date(exam.ends_at).getTime() - Date.now()) / 1000));
      setRemaining(secs);
      if (secs <= 0 && !submittedRef.current && !myResult) doSubmit();
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, exam?.ends_at]);

  const doSubmit = () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    let score = 0;
    questions.forEach((nq, i) => { if (gradeAnswer(nq, answers[i])) score += 1; });
    onSubmit(score, questions.length);
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  // ── IDLE: host config / waiting ──
  if (phase === "idle" || !exam) {
    return (
      <div className="surface p-6 sm:p-8 text-center" data-testid="room-exam-idle">
        <div className="w-12 h-12 rounded-2xl bg-[rgba(124,92,255,0.14)] flex items-center justify-center mx-auto mb-4">
          <Timer size={22} className="text-[#B8A4FF]" />
        </div>
        <h3 className="text-[18px] font-semibold mb-1">Timed Exam</h3>
        <p className="text-[13.5px] text-[#B0AEBC] mb-6">
          Everyone in the room takes the same exam against a shared countdown. Scores appear on a live leaderboard.
        </p>
        {isOwner ? (
          quizCount === 0 ? (
            <p className="text-[13px] text-[#FCA5A5]">No quiz questions available to build an exam.</p>
          ) : (
            <div className="max-w-sm mx-auto text-left space-y-4">
              <div>
                <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Duration</label>
                <div className="grid grid-cols-3 gap-2" data-testid="room-exam-duration">
                  {[{ s: 300, l: "5 min" }, { s: 600, l: "10 min" }, { s: 900, l: "15 min" }].map((d) => (
                    <button key={d.s} onClick={() => setDuration(d.s)} data-testid={`room-exam-dur-${d.s}`}
                      className="py-2 rounded-xl text-[13px] font-medium border transition-colors"
                      style={duration === d.s
                        ? { background: "rgba(124,92,255,0.18)", borderColor: "rgba(124,92,255,0.4)", color: "#fff" }
                        : { background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.08)", color: "#B0AEBC" }}>
                      {d.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Questions ({Math.min(count, quizCount)})</label>
                <input type="range" min={3} max={Math.max(3, quizCount)} value={Math.min(count, quizCount)}
                  onChange={(e) => setCount(parseInt(e.target.value, 10))}
                  data-testid="room-exam-count" className="w-full accent-[#7C5CFF]" />
                <p className="text-[11px] text-[#8E8B99] mt-1">{quizCount} question{quizCount === 1 ? "" : "s"} available</p>
              </div>
              <button onClick={() => onStart(duration, Math.min(count, quizCount))} data-testid="room-exam-start-btn"
                className="btn btn-primary w-full py-3 flex items-center justify-center gap-2">
                <Play size={15} /> Start exam for everyone
              </button>
            </div>
          )
        ) : (
          <p className="text-[13px] text-[#8E8B99]" data-testid="room-exam-waiting">Waiting for the host to start a timed exam…</p>
        )}
      </div>
    );
  }

  const done = phase === "done" || !!myResult;

  return (
    <div data-testid="room-exam-active">
      {/* Sticky exam header: timer + leaderboard count + host controls */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full"
          style={{ background: remaining <= 30 ? "rgba(252,165,165,0.14)" : "rgba(124,92,255,0.14)" }}>
          <Timer size={15} className={remaining <= 30 ? "text-[#FCA5A5]" : "text-[#B8A4FF]"} />
          <span className="font-mono font-semibold tabular-nums text-[15px]" data-testid="room-exam-timer"
            style={{ color: remaining <= 30 ? "#FCA5A5" : "#fff" }}>{mmss(remaining)}</span>
        </div>
        {isOwner && (
          <button onClick={onEnd} data-testid="room-exam-end-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border border-white/[0.1] text-[#B0AEBC] hover:text-white">
            <Square size={12} /> End exam
          </button>
        )}
      </div>

      {!done ? (
        <div className="space-y-4">
          {questions.map((nq, i) => (
            <div key={i} className="surface p-5" data-testid={`room-exam-q-${i}`}>
              <div className="flex gap-2.5 mb-3">
                <span className="text-[13px] font-bold text-[#B8A4FF] tabular-nums">{i + 1}.</span>
                <p className="text-[15px] font-medium leading-snug flex-1">{nq.text}</p>
              </div>
              {nq.type === "mcq" && (
                <div className="space-y-2 pl-7">
                  {nq.options.map((o) => (
                    <button key={o.label} onClick={() => setAnswers((a) => ({ ...a, [i]: o.label }))}
                      data-testid={`room-exam-q-${i}-opt-${o.label}`}
                      className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13.5px] border transition-colors"
                      style={answers[i] === o.label
                        ? { background: "rgba(124,92,255,0.16)", borderColor: "rgba(124,92,255,0.4)", color: "#fff" }
                        : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.07)", color: "#B0AEBC" }}>
                      <span className="font-mono font-semibold">{o.label}</span><span>{o.text}</span>
                    </button>
                  ))}
                </div>
              )}
              {nq.type === "tf" && (
                <div className="flex gap-2 pl-7">
                  {["True", "False"].map((v) => (
                    <button key={v} onClick={() => setAnswers((a) => ({ ...a, [i]: v }))}
                      data-testid={`room-exam-q-${i}-tf-${v}`}
                      className={`px-5 py-2 rounded-xl text-[13.5px] font-medium border transition-colors ${
                        answers[i] === v
                          ? ""
                          : "border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] text-[#5A5A66] dark:text-[#B0AEBC]"
                      }`}
                      style={answers[i] === v
                        ? { background: "rgba(124,92,255,0.16)", borderColor: "rgba(124,92,255,0.4)", color: "#7C5CFF" }
                        : undefined}>
                      {v}
                    </button>
                  ))}
                </div>
              )}
              {nq.type !== "mcq" && nq.type !== "tf" && (
                <input value={answers[i] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))}
                  data-testid={`room-exam-q-${i}-input`} placeholder="Type your answer…"
                  className="input ml-7" style={{ width: "calc(100% - 1.75rem)" }} />
              )}
            </div>
          ))}
          <button onClick={doSubmit} data-testid="room-exam-submit-btn"
            className="btn btn-primary w-full py-3 flex items-center justify-center gap-2">
            <Send size={15} /> Submit exam
          </button>
        </div>
      ) : (
        <div data-testid="room-exam-results">
          {myResult && (
            <div className="surface p-7 text-center mb-5">
              <p className="text-[12px] uppercase tracking-widest text-[#8E8B99] mb-2">Your score</p>
              <p className="text-[42px] font-bold text-white leading-none" data-testid="room-exam-my-score">
                {myResult.score}<span className="text-[#8E8B99] text-[24px]"> / {myResult.total}</span>
              </p>
            </div>
          )}
          <div className="surface p-5">
            <div className="flex items-center gap-2 mb-4">
              <Trophy size={16} className="text-[#F59E0B]" />
              <h3 className="text-[15px] font-semibold">Live leaderboard</h3>
            </div>
            {leaderboard.length === 0 ? (
              <p className="text-[13px] text-[#8E8B99]">Waiting for results…</p>
            ) : (
              <div className="space-y-2" data-testid="room-exam-leaderboard">
                {leaderboard.map((r, i) => (
                  <div key={r.user_id} className="flex items-center justify-between px-3 py-2 rounded-xl"
                    style={{ background: r.user_id === currentUserId ? "rgba(124,92,255,0.12)" : "rgba(255,255,255,0.03)" }}>
                    <div className="flex items-center gap-2.5">
                      <span className="text-[13px] font-bold text-[#8E8B99] w-5 tabular-nums">{i + 1}</span>
                      <span className="text-[13.5px] font-medium">{r.name}{r.user_id === currentUserId ? " (you)" : ""}</span>
                    </div>
                    <span className="text-[13.5px] font-semibold text-[#B8A4FF] tabular-nums">{r.score}/{r.total}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
